/**
 * Mensajes bilingües (UI + voz) para reportes Sharma.
 * Locale activo: Redux `ui.conversationLocale` — `'en'` por defecto; `'es'` cuando el usuario
 * pide español (ver `setConversationLocale` en IaDashboard al detectar la petición).
 */

import { store } from "../redux/store";

/** @typedef {'en' | 'es'} SharmaLocale */
/** @typedef {'chart' | 'table'} SharmaReportKind */

export const REASON_CODES = {
  HTTP_ERROR: "http_error",
  INVALID_PORTFOLIO: "invalid_portfolio",
  INTEGRATION_ERROR: "integration_error",
  PORTFOLIO_ERROR: "portfolio_error",
  NO_OVERLAP_DATA: "no_overlap_data",
  EMPTY_STRATEGIES_AND_PORTFOLIO: "empty_strategies_and_portfolio",
  EMPTY_TABLE: "empty_table",
  EMPTY_CHART: "empty_chart",
  NO_DRAWDOWN_DATA: "no_drawdown_data",
  MARGINAL_RISK_BETA_EMPTY: "marginal_risk_beta_empty",
  RISK_PROFILE_EMPTY: "risk_profile_empty",
  RISK_ATTRIBUTION_EMPTY: "risk_attribution_empty",
  UNKNOWN_FORMAT: "unknown_format",
};

const DETAIL_EN = {
  [REASON_CODES.HTTP_ERROR]: "the report service returned an error",
  [REASON_CODES.INVALID_PORTFOLIO]: "no valid working portfolio is selected",
  [REASON_CODES.INTEGRATION_ERROR]: "the response was invalid or incomplete",
  [REASON_CODES.PORTFOLIO_ERROR]: "the portfolio reported an error",
  [REASON_CODES.NO_OVERLAP_DATA]: "there is not enough overlapping data for this period",
  [REASON_CODES.EMPTY_STRATEGIES_AND_PORTFOLIO]:
    "no strategy rows or portfolio totals were available",
  [REASON_CODES.EMPTY_TABLE]: "no table rows or columns could be built",
  [REASON_CODES.EMPTY_CHART]: "no chart categories or series could be built",
  [REASON_CODES.NO_DRAWDOWN_DATA]: "no drawdown values were provided",
  [REASON_CODES.MARGINAL_RISK_BETA_EMPTY]: "no marginal beta values were provided",
  [REASON_CODES.RISK_PROFILE_EMPTY]: "no risk profile axis had a numeric value",
  [REASON_CODES.RISK_ATTRIBUTION_EMPTY]: "no strategy had risk attribution metrics",
  [REASON_CODES.UNKNOWN_FORMAT]: "the report format was not recognized",
};

const DETAIL_ES = {
  [REASON_CODES.HTTP_ERROR]: "el servicio de reportes respondió con error",
  [REASON_CODES.INVALID_PORTFOLIO]: "no hay un portafolio de trabajo válido seleccionado",
  [REASON_CODES.INTEGRATION_ERROR]: "la respuesta no fue válida o está incompleta",
  [REASON_CODES.PORTFOLIO_ERROR]: "el portafolio devolvió un error",
  [REASON_CODES.NO_OVERLAP_DATA]:
    "no hay datos con solape suficiente para este periodo",
  [REASON_CODES.EMPTY_STRATEGIES_AND_PORTFOLIO]:
    "no hay filas de estrategia ni totales de portafolio disponibles",
  [REASON_CODES.EMPTY_TABLE]: "no se pudieron construir filas ni columnas de tabla",
  [REASON_CODES.EMPTY_CHART]: "no se pudieron construir categorías ni series",
  [REASON_CODES.NO_DRAWDOWN_DATA]: "no se recibieron valores de drawdown",
  [REASON_CODES.MARGINAL_RISK_BETA_EMPTY]: "no se recibieron betas marginales",
  [REASON_CODES.RISK_PROFILE_EMPTY]:
    "ningún eje del perfil de riesgo tenía un valor numérico",
  [REASON_CODES.RISK_ATTRIBUTION_EMPTY]:
    "ninguna estrategia tenía métricas de atribución",
  [REASON_CODES.UNKNOWN_FORMAT]: "el formato del reporte no fue reconocido",
};

/** @returns {SharmaLocale} */
export function resolveActiveLocale() {
  try {
    const loc = store.getState()?.ui?.conversationLocale;
    return loc === "es" ? "es" : "en";
  } catch {
    return "en";
  }
}

/**
 * @param {SharmaLocale} locale
 * @param {string} reasonCode
 * @param {string} [extraDetail]
 */
export function reasonCodeToDetail(locale, reasonCode, extraDetail) {
  const map = locale === "es" ? DETAIL_ES : DETAIL_EN;
  const base = map[reasonCode] || map[REASON_CODES.UNKNOWN_FORMAT];
  if (extraDetail && String(extraDetail).trim()) {
    return locale === "es"
      ? `${base} (${String(extraDetail).trim()})`
      : `${base} (${String(extraDetail).trim()})`;
  }
  return base;
}

/** @param {unknown} err */
export function errorToReasonCode(err) {
  const msg = err?.message || String(err || "");
  if (msg.includes("HTTP")) return REASON_CODES.HTTP_ERROR;
  if (msg.includes("Failed to fetch") || msg.includes("NetworkError")) {
    return REASON_CODES.HTTP_ERROR;
  }
  if (msg.includes("drawdowns")) return REASON_CODES.NO_DRAWDOWN_DATA;
  if (msg.includes("ytd_strategy") || msg.includes("empty_strategies")) {
    return REASON_CODES.EMPTY_STRATEGIES_AND_PORTFOLIO;
  }
  if (msg.includes("risk_attribution")) return REASON_CODES.RISK_ATTRIBUTION_EMPTY;
  if (msg.includes("marginal_risk_beta")) return REASON_CODES.MARGINAL_RISK_BETA_EMPTY;
  if (msg.includes("risk_profile")) return REASON_CODES.RISK_PROFILE_EMPTY;
  if (msg.includes("value_at_risk")) return REASON_CODES.PORTFOLIO_ERROR;
  if (msg.includes("business_cycle")) return REASON_CODES.UNKNOWN_FORMAT;
  return REASON_CODES.UNKNOWN_FORMAT;
}

/**
 * @param {SharmaLocale} [locale]
 * @param {string} reasonCode
 * @param {string} [detail]
 */
export function formatSharmaChartFailure(locale, reasonCode, detail) {
  const loc = locale ?? resolveActiveLocale();
  const d = detail ?? reasonCodeToDetail(loc, reasonCode);
  if (loc === "es") {
    return `No se pudo obtener la gráfica: ${d}.`;
  }
  return `The chart could not be loaded: ${d}.`;
}

/**
 * @param {SharmaLocale} [locale]
 * @param {string} reasonCode
 * @param {string} [detail]
 */
export function formatSharmaTableFailure(locale, reasonCode, detail) {
  const loc = locale ?? resolveActiveLocale();
  const d = detail ?? reasonCodeToDetail(loc, reasonCode);
  if (loc === "es") {
    return `No se pudo obtener la tabla: ${d}.`;
  }
  return `The table could not be loaded: ${d}.`;
}

/**
 * @param {SharmaLocale} [locale]
 * @param {SharmaReportKind} reportKind
 * @param {string} [reportName]
 */
export function formatSharmaPartialSuccess(locale, reportKind, reportName) {
  const loc = locale ?? resolveActiveLocale();
  if (loc === "es") {
    if (reportKind === "chart") {
      return "Se graficó con datos parciales; algunas series o puntos se omitieron porque el API no envió valores.";
    }
    return "Se mostró la tabla con datos parciales; algunas columnas o filas se omitieron porque el API no envió valores.";
  }
  if (reportKind === "chart") {
    return "The chart is shown with partial data; some series or points were omitted because the API did not provide values.";
  }
  return "The table is shown with partial data; some columns or rows were omitted because the API did not provide values.";
}

/** @param {object|null|undefined} payload */
export function isPartialSharmaResult(payload) {
  return Boolean(payload && payload.partial === true);
}

/**
 * Normaliza payload de adaptador para UI/voz.
 * @param {object|null|undefined} payload
 * @param {SharmaReportKind} kind
 */
export function enrichSharmaPayload(payload, kind) {
  if (!payload || typeof payload !== "object") {
    return payload;
  }
  const locale = resolveActiveLocale();
  let out = { ...payload };

  if (out.reasonCode && !out.loadError) {
    const detail =
      out.reasonDetail != null
        ? String(out.reasonDetail)
        : reasonCodeToDetail(locale, out.reasonCode);
    out.loadError =
      kind === "chart"
        ? formatSharmaChartFailure(locale, out.reasonCode, detail)
        : formatSharmaTableFailure(locale, out.reasonCode, detail);
  }

  if (out.partial === true && !out.partialNotice) {
    out.partialNotice = formatSharmaPartialSuccess(
      locale,
      kind,
      out.title || ""
    );
  }

  return out;
}

/** Detección mínima de petición de español en el turno del usuario. */
export function detectSpanishLocaleRequest(text) {
  if (!text || typeof text !== "string") return false;
  const t = text.toLowerCase();
  return (
    /\b(español|espanol|spanish)\b/.test(t) ||
    /\b(en español|in spanish|habla en español|hablar en español|responde en español|answer in spanish|speak spanish)\b/.test(
      t
    )
  );
}

/** @param {unknown} err */
export function formatSharmaChartFailureFromError(err) {
  return formatSharmaChartFailure(
    resolveActiveLocale(),
    errorToReasonCode(err),
    err?.message ? String(err.message) : undefined
  );
}

/** @param {unknown} err */
export function formatSharmaTableFailureFromError(err) {
  return formatSharmaTableFailure(
    resolveActiveLocale(),
    errorToReasonCode(err),
    err?.message ? String(err.message) : undefined
  );
}
